import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  fetchOrganizations,
  fetchOrganizationsForUserSettings,
} from "../../actions";
import { filterOrganizationByVisibilityThunk } from "../../actions";
import { cnpjMask } from "../../../utils/formatters";

export type TOrganization = {
  account: { uuid: string };
  cnpj: string;
  created_at: string;
  is_active: boolean;
  default_nexaas_stock_id: string;
  company_name: string;
  updated_at: string;
  uuid: string;
  permissions: {
    fintera: boolean;
    nexaas_omni: boolean;
    organization_management: boolean;
    user_management: boolean;
  };
};

export type TOrganizationData = {
  results: TOrganization[];
  /**
   * Total number of organizations (pagination).
   * Backend supports this for paginated queries.
   * TODO: Use this for paginated UI if/when needed. Do not remove.
   */
  count: number;
  /**
   * Current page in pagination. Backend supports this for paginated queries.
   * TODO: Use this for paginated UI if/when needed. Do not remove.
   */
  current_page: number;
  /**
   * List of pagination pages. Backend supports this for paginated queries.
   * TODO: Use this for paginated UI if/when needed. Do not remove.
   */
  pages: any[];
  /**
   * TODO: Audit all usages of organization state for duplication and persistency issues.
   * Migration: Ensure organization context is only stored in one canonical place (globalState.selectOrganization).
   */
};

// State for organizations in /settings/users/:id route
type TSettingsUserOrganizationsState = {
  loading: boolean;
  error: null | string;
  data: TOrganizationData;
};

const initialSettingsUserOrganizationsState: TSettingsUserOrganizationsState = {
  loading: false,
  error: "",
  data: {
    count: -1,
    current_page: 0,
    pages: [],
    results: [],
  },
};

type TInitialState = {
  loading: boolean;
  error: null | string;
  data: TOrganizationData;
  selectedOrganization: TOrganization | null;
  lastAccessedOrganizations: TOrganization[];
  previouslyLoadedOrganizations: TOrganization[];
};

const initialState: TInitialState = {
  loading: false,
  error: "",
  data: {
    count: -1,
    current_page: 0,
    pages: [],
    results: [],
  },
  selectedOrganization: null,
  lastAccessedOrganizations: [],
  previouslyLoadedOrganizations: [],
};

// Separate state for organizations displayed in settings
type TSettingsOrganizationsState = {
  loading: boolean;
  error: null | string;
  data: TOrganizationData;
};

const initialSettingsOrganizationsState: TSettingsOrganizationsState = {
  loading: false,
  error: "",
  data: {
    count: -1,
    current_page: 0,
    pages: [],
    results: [],
  },
};

const organizationsDataSlice = createSlice({
  name: "organizations",
  initialState: initialState,
  reducers: {
    setOrganizations: (state, action: PayloadAction<TOrganizationData>) => {
      // Merge organizations with lastAccessedOrganizations, deduplicating by uuid
      const orgs = action.payload.results;
      const lastAccessed = state.lastAccessedOrganizations || [];
      const mergedResults = [
        ...orgs,
        ...lastAccessed.filter((org) => !orgs.some((o) => o.uuid === org.uuid)),
      ];
      state.data = {
        ...action.payload,
        results: mergedResults,
      };
    },
    setOrganizationsOnSearch: (
      state,
      action: PayloadAction<TOrganizationData>
    ) => {
      state.data = {
        ...action.payload,
        results: action.payload.results,
      };
    },
    setLastAccessedOrganizations: (
      state,
      action: PayloadAction<TOrganization[]>
    ) => {
      const lastAccessedOrgs = (action.payload || []).map((organization) => {
        const org = { ...organization };
        org.cnpj = cnpjMask(org.cnpj);
        return org;
      });

      state.lastAccessedOrganizations = lastAccessedOrgs;
    },
    setSelectedOrganization: (
      state,
      action: PayloadAction<TOrganization | null>
    ) => {
      state.selectedOrganization = action.payload;
    },
    setPreviouslyLoadedOrganizations: (
      state,
      action: PayloadAction<TOrganization[]>
    ) => {
      state.previouslyLoadedOrganizations = action.payload;
    },
    clearOrganizations: (state) => {
      state.data = { count: -1, current_page: 0, pages: [], results: [] };
      state.selectedOrganization = null;
      state.lastAccessedOrganizations = [];
      state.previouslyLoadedOrganizations = [];
      state.loading = false;
      state.error = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizations.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        state.loading = false;
        // Merge new results with existing results, deduplicating by uuid
        const existingResults = state.data?.results || [];
        const newResults = action.payload?.results || [];
        const mergedResults = [
          ...existingResults,
          ...newResults.filter(
            (org) => !existingResults.some((o) => o.uuid === org.uuid)
          ),
        ];
        state.data = {
          ...action.payload,
          results: mergedResults,
        };
      })
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "error";
      });
  },
});

const settingsOrganizationsSlice = createSlice({
  name: "settingsOrganizations",
  initialState: initialSettingsOrganizationsState,
  reducers: {
    setSettingsOrganizations: (
      state,
      action: PayloadAction<TOrganizationData>
    ) => {
      state.data = action.payload;
    },
    clearSettingsOrganizations: (state) => {
      state.data = { count: -1, current_page: 0, pages: [], results: [] };
      state.error = "";
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(filterOrganizationByVisibilityThunk.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(
        filterOrganizationByVisibilityThunk.fulfilled,
        (state, action) => {
          state.loading = false;
          state.data = action.payload;
        }
      )
      .addCase(
        filterOrganizationByVisibilityThunk.rejected,
        (state, action) => {
          state.loading = false;
          state.error = action.error.message ?? "error";
        }
      );
  },
});

const settingsUserOrganizationsSlice = createSlice({
  name: "settingsUserOrganizations",
  initialState: initialSettingsUserOrganizationsState,
  reducers: {
    setSettingsUserOrganizations: (
      state,
      action: PayloadAction<TOrganizationData>
    ) => {
      state.data = action.payload;
    },
    clearSettingsUserOrganizations: (state) => {
      state.data = { count: -1, current_page: 0, pages: [], results: [] };
      state.error = "";
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationsForUserSettings.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(fetchOrganizationsForUserSettings.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchOrganizationsForUserSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "error";
      });
  },
});
export const { setSettingsUserOrganizations, clearSettingsUserOrganizations } =
  settingsUserOrganizationsSlice.actions;

export const settingsUserOrganizationsReducer =
  settingsUserOrganizationsSlice.reducer;

export const {
  setOrganizations,
  setOrganizationsOnSearch,
  setLastAccessedOrganizations,
  setSelectedOrganization,
  setPreviouslyLoadedOrganizations,
  clearOrganizations,
} = organizationsDataSlice.actions;
export const { setSettingsOrganizations, clearSettingsOrganizations } =
  settingsOrganizationsSlice.actions;

export const settingsOrganizationsReducer = settingsOrganizationsSlice.reducer;
export default organizationsDataSlice.reducer;
